// =====================================================================
// CARD PRINTING SECTION - USER INTERFACE
// =====================================================================
// Purpose: User-facing interface for Windows server card printing operations
// Features: Screenshot capture, location selector, fullscreen zoom
// =====================================================================

import React, { useState, useEffect, useRef } from 'react';
import './CardPrintingSection.css';

interface CardPrintingSectionProps {
  onClose?: () => void;
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
}

export const CardPrintingSection: React.FC<CardPrintingSectionProps> = ({ onClose }) => {
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [lastCaptured, setLastCaptured] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // S3 Sync state
  const [isS3Syncing, setIsS3Syncing] = useState(false);
  const [syncStats, setSyncStats] = useState({ total: 0, downloaded: 0, errors: 0 });
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [consoleLogs, setConsoleLogs] = useState<LogEntry[]>([]);
  const [emptyBucketSync, setEmptyBucketSync] = useState(false);
  const consoleRef = useRef<HTMLDivElement>(null);

  // Print Cards state
  const [isPrinting, setIsPrinting] = useState(false);
  const [printLogs, setPrintLogs] = useState<LogEntry[]>([]);
  const [lastPrintTime, setLastPrintTime] = useState<Date | null>(null);
  const [printStatus, setPrintStatus] = useState<{ phase: string; message: string; success: boolean | null } | null>(null);
  const printConsoleRef = useRef<HTMLDivElement>(null);
  
  // Image Transfer state
  const [isTransferring, setIsTransferring] = useState(false);
  const [printWorkflowComplete, setPrintWorkflowComplete] = useState(false);
  const [transferLogs, setTransferLogs] = useState<string[]>([]);
  const transferConsoleRef = useRef<HTMLDivElement>(null);

  // Workflow State Machine
  const [hasCompletedS3Sync, setHasCompletedS3Sync] = useState(false);

  // Accordion state
  const [isScreenshotExpanded, setIsScreenshotExpanded] = useState(false);
  const [isS3Expanded, setIsS3Expanded] = useState(false);
  const [isPrintExpanded, setIsPrintExpanded] = useState(false);

  const token = localStorage.getItem('egl_token');

  // Fetch available locations for user
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch('/api/card-printing/locations', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success && data.data.locations.length > 0) {
        setAvailableLocations(data.data.locations);
        setSelectedLocation(data.data.locations[0]); // Auto-select first location
      } else {
        setError('No locations available. Please contact administrator.');
      }
    } catch (err) {
      setError('Failed to load locations');
      console.error(err);
    }
  };

  const captureScreenshot = async () => {
    if (!selectedLocation) {
      setError('Please select a location');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/card-printing/screenshot/${selectedLocation}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to capture screenshot: ${response.status}`);
      }

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      
      // Revoke previous image URL to prevent memory leaks
      if (screenshot) {
        URL.revokeObjectURL(screenshot);
      }

      setScreenshot(imageUrl);
      setLastCaptured(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to capture screenshot');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    
    if (seconds < 10) return 'Just now';
    if (seconds < 60) return `${seconds} seconds ago`;
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  };

  const getFreshnessIndicator = (): { color: string; emoji: string; text: string } => {
    if (!lastCaptured) return { color: '#9ca3af', emoji: '⚪', text: 'No capture yet' };
    
    const seconds = Math.floor((new Date().getTime() - lastCaptured.getTime()) / 1000);
    
    if (seconds < 60) return { color: '#10b981', emoji: '🟢', text: 'Fresh' };
    if (seconds < 300) return { color: '#f59e0b', emoji: '🟡', text: 'Recent' };
    return { color: '#ef4444', emoji: '🔴', text: 'Stale' };
  };

  const openFullscreen = () => {
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  // Handle ESC key to close fullscreen
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        closeFullscreen();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isFullscreen]);

  // Poll sync status and logs while syncing
  useEffect(() => {
    if (!isS3Syncing || !selectedLocation) return;

    const pollInterval = setInterval(async () => {
      try {
        // Get sync status
        const statusRes = await fetch(`/api/card-printing/sync/${selectedLocation}/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (statusRes.ok) {
          const statusData = await statusRes.json();
          const stats = statusData.stats || { total: 0, downloaded: 0, errors: 0 };
          setSyncStats(stats);

          // If sync is complete, stop polling
          if (!statusData.is_syncing) {
            setIsS3Syncing(false);
            setLastSyncTime(new Date());
            
            // Check if bucket was empty
            if (stats.total === 0 && stats.downloaded === 0 && stats.errors === 0) {
              setEmptyBucketSync(true);
              // Empty bucket means no cards to print - mark as complete
              setHasCompletedS3Sync(true);
            } else {
              setEmptyBucketSync(false);
              // Successfully downloaded cards - mark as complete
              setHasCompletedS3Sync(true);
            }
          }
        }

        // Get logs
        const logsRes = await fetch(`/api/card-printing/logs/${selectedLocation}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (logsRes.ok) {
          const logsData = await logsRes.json();
          setConsoleLogs(logsData.logs || []);

          // Auto-scroll to bottom
          if (consoleRef.current) {
            consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
          }
        }
      } catch (err) {
        console.error('Failed to poll sync status/logs:', err);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [isS3Syncing, selectedLocation, token]);

  const startS3Sync = async () => {
    if (!selectedLocation) {
      setError('Please select a location');
      return;
    }

    try {
      setError(null);
      setIsS3Syncing(true);
      setSyncStats({ total: 0, downloaded: 0, errors: 0 });
      setConsoleLogs([]);
      setEmptyBucketSync(false);
      setHasCompletedS3Sync(false); // Reset workflow state

      const response = await fetch(`/api/card-printing/sync/${selectedLocation}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start S3 sync');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start S3 sync');
      setIsS3Syncing(false);
      console.error(err);
    }
  };

  const cancelS3Sync = async () => {
    if (!selectedLocation) return;

    try {
      await fetch(`/api/card-printing/sync/${selectedLocation}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setIsS3Syncing(false);
    } catch (err) {
      console.error('Failed to cancel sync:', err);
    }
  };

  // Auto-scroll print console when logs update
  useEffect(() => {
    if (printConsoleRef.current) {
      const scrollElement = printConsoleRef.current;
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }, 0);
    }
  }, [printLogs]);
  
  // Auto-scroll transfer console when logs update
  useEffect(() => {
    if (transferConsoleRef.current) {
      const scrollElement = transferConsoleRef.current;
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }, 0);
    }
  }, [transferLogs]);

  // Poll print status and logs while printing
  useEffect(() => {
    if (!isPrinting || !selectedLocation) return;

    const pollInterval = setInterval(async () => {
      try {
        // Get print logs first (always available)
        const logsRes = await fetch(`/api/card-printing/print/${selectedLocation}/logs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        let logs: LogEntry[] = [];
        if (logsRes.ok) {
          const logsData = await logsRes.json();
          logs = logsData.logs || [];
          setPrintLogs(logs);
        }

        // Get print status (may be 404 if idle/completed)
        const statusRes = await fetch(`/api/card-printing/print/${selectedLocation}/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setPrintStatus({
            phase: statusData.phase,
            message: statusData.message,
            success: statusData.success
          });

          // If print is complete or failed, stop polling
          if (statusData.phase === 'complete' || statusData.phase === 'failed') {
            setIsPrinting(false);
            setLastPrintTime(new Date());
            
            // Show transfer button if workflow completed successfully
            if (statusData.phase === 'complete') {
              setPrintWorkflowComplete(true);
            }
          }
        } else if (statusRes.status === 404) {
          // Status not found - workflow may have completed or expired
          // Check logs for completion markers
          const lastLog = logs[logs.length - 1];
          if (lastLog) {
            const isComplete = logs.some(log => log.message.includes('=== PrintStudio Unified Workflow - COMPLETE ==='));
            const isFailed = logs.some(log => log.message.includes('✗ FAILED:'));

            if (isComplete || isFailed) {
              // Workflow finished, stop polling
              setIsPrinting(false);
              setLastPrintTime(new Date());
              setPrintStatus({
                phase: isComplete ? 'complete' : 'failed',
                message: isComplete ? 'Workflow completed successfully' : 'Workflow failed',
                success: isComplete ? true : false
              });

              // Workflow completed - Phase 7 already sent Ctrl+P automatically
              // Show transfer button if successful
              if (isComplete) {
                setPrintWorkflowComplete(true);
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to poll print status/logs:', err);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [isPrinting, selectedLocation, token]);

  const startPrint = async () => {
    if (!selectedLocation) {
      setError('Please select a location');
      return;
    }

    try {
      setError(null);
      setIsPrinting(true);
      setPrintLogs([]);
      setPrintStatus(null);

      const response = await fetch(`/api/card-printing/print/${selectedLocation}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.detail || 'Failed to start print workflow');
      }

      const data = await response.json();
      setPrintStatus({
        phase: 'started',
        message: data.message || 'Print workflow started',
        success: null
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start print workflow');
      setIsPrinting(false);
      console.error(err);
    }
  };

  // NOTE: sendCtrlP function removed - Phase 7 of PrintStudioWorkflow.ahk
  // now automatically sends Left Ctrl+P at the end of the workflow

  const proceedToPrinting = () => {
    // Collapse S3 Sync accordion and open Print Cards accordion
    setIsS3Expanded(false);
    setIsPrintExpanded(true);
    
    // Scroll to Print Cards section
    setTimeout(() => {
      const printSection = document.querySelector('.accordion-section:nth-child(3)');
      if (printSection) {
        printSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };
  
  const addTransferLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setTransferLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };
  
  const transferImagesToPhotos = async () => {
    if (!selectedLocation) {
      addTransferLog('❌ No location selected');
      alert('❌ No location selected');
      return;
    }
    
    if (isTransferring) {
      return;
    }
    
    try {
      setIsTransferring(true);
      setTransferLogs([]); // Clear previous logs
      
      addTransferLog('🚀 Starting image transfer...');
      addTransferLog(`📂 Source: Staging folder`);
      addTransferLog(`📁 Destination: Photos folder`);
      addTransferLog('');
      
      const response = await fetch('/api/card-printing/printstudio/transfer-images', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-API-Key': token || ''
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail?.message || errorData.detail || `Transfer failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      addTransferLog('');
      addTransferLog('✅ Transfer complete!');
      addTransferLog(`📦 Files moved: ${data.files_moved}`);
      
      if (data.duplicates_renamed > 0) {
        addTransferLog(`🔄 Duplicates renamed: ${data.duplicates_renamed}`);
      }
      
      if (data.failed_files && data.failed_files.length > 0) {
        addTransferLog('');
        addTransferLog(`⚠️ Failed files: ${data.failed_files.length}`);
        data.failed_files.forEach((file: string) => {
          addTransferLog(`  ✗ ${file}`);
        });
      } else {
        addTransferLog('✓ All images successfully transferred!');
      }
      
      addTransferLog('');
      addTransferLog('🎉 Transfer workflow complete!');
      
      // Show success message
      let message = `✅ Transfer Complete!\n\n`;
      message += `Files moved: ${data.files_moved}\n`;
      
      if (data.duplicates_renamed > 0) {
        message += `Duplicates renamed: ${data.duplicates_renamed}\n`;
      }
      
      if (data.failed_files && data.failed_files.length > 0) {
        message += `\n⚠️ Failed files: ${data.failed_files.length}\n`;
        message += data.failed_files.join(', ');
      } else {
        message += `\n✓ All images successfully transferred to Photos folder!`;
      }
      
      alert(message);
      
      // Reset workflow flags after short delay
      setTimeout(() => {
        setPrintWorkflowComplete(false);
        setHasCompletedS3Sync(false);
      }, 2000);
      
    } catch (error: any) {
      console.error('Transfer failed:', error);
      const errorMsg = error.message || 'Unknown error';
      
      addTransferLog('');
      addTransferLog('❌ Transfer failed!');
      addTransferLog(`Error: ${errorMsg}`);
      
      alert(`❌ Transfer failed:\n\n${errorMsg}`);
    } finally {
      setIsTransferring(false);
    }
  };

  const freshness = getFreshnessIndicator();
  
  return (
    <>
    <div className="card-printing-section">
      {/* Header */}
        <div className="cp-header">
          <h2>🖨️ Card Printing Control</h2>
        </div>
        
        {/* Location Selector (if user has multiple locations) */}
        {availableLocations.length > 1 && (
          <div className="cp-location-selector">
            <label>Location:</label>
              <select 
                value={selectedLocation} 
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="cp-location-dropdown"
            >
              {availableLocations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
          )}
          
        {/* Single location display (if user has only one) */}
        {availableLocations.length === 1 && (
          <div className="cp-location-display">
            <span className="location-badge">{selectedLocation}</span>
        </div>
      )}
      
        {/* Error Display */}
        {error && (
          <div className="cp-error">
            ⚠️ {error}
        </div>
      )}
      
        {/* Screenshot Accordion */}
        <div className="accordion-section">
          <div 
            className="accordion-header" 
            onClick={() => {
              setIsScreenshotExpanded(!isScreenshotExpanded);
              if (!isScreenshotExpanded) {
                setIsS3Expanded(false);
                setIsPrintExpanded(false);
              }
            }}
          >
            <span className="accordion-title">📺 View Server Display</span>
            <span className="accordion-icon">{isScreenshotExpanded ? '▼' : '▶'}</span>
          </div>
          
          {isScreenshotExpanded && (
            <div className="accordion-content">
              {/* Screenshot Display Area */}
              <div className="cp-screenshot-container">
          {!screenshot && !loading && (
            <div className="cp-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">📸</div>
                <div className="placeholder-text">No screenshot yet</div>
                <div className="placeholder-subtext">Click button below to capture Windows desktop screen</div>
              </div>
            </div>
          )}
          
          {loading && (
            <div className="cp-loading">
              <div className="loading-spinner"></div>
              <div className="loading-text">⏳ Capturing screenshot...</div>
            </div>
          )}
          
          {screenshot && !loading && (
            <div className="cp-screenshot-wrapper" onClick={openFullscreen}>
              <img 
                src={screenshot} 
                alt="Windows Desktop Screenshot" 
                className="cp-screenshot-image"
              />
              <div className="cp-zoom-hint">
                🔍 Click to view fullscreen
              </div>
            </div>
          )}
        </div>
        
        {/* Capture Info */}
        {lastCaptured && (
          <div className="cp-capture-info">
            <span className="freshness-indicator" style={{ color: freshness.color }}>
              {freshness.emoji} {freshness.text}
            </span>
            <span className="capture-time">
              Last captured: {formatTimeAgo(lastCaptured)}
                </span>
            </div>
          )}

        {/* Capture Button */}
        <div className="cp-actions">
          <button 
            className="cp-capture-btn"
            onClick={captureScreenshot}
            disabled={loading || !selectedLocation}
          >
            {loading ? '⏳ Capturing...' : '📸 Take Screenshot'}
          </button>
        </div>
            </div>
          )}
        </div>

        {/* S3 Sync Accordion */}
        <div className="accordion-section">
          <div 
            className="accordion-header" 
            onClick={() => {
              setIsS3Expanded(!isS3Expanded);
              if (!isS3Expanded) {
                setIsScreenshotExpanded(false);
                setIsPrintExpanded(false);
              }
            }}
          >
            <span className="accordion-title">☁️ S3 Sync Control</span>
            <span className="accordion-icon">{isS3Expanded ? '▼' : '▶'}</span>
          </div>
          
          {isS3Expanded && (
            <div className="accordion-content">
              {/* S3 Sync Section */}
        <div className="s3-sync-section">
          {/* Sync Controls */}
          <div className="s3-sync-controls">
            <button 
              className="s3-sync-btn"
              onClick={startS3Sync}
              disabled={isS3Syncing || !selectedLocation}
            >
              {isS3Syncing ? '⏳ Syncing...' : '🔄 Start S3 Sync'}
            </button>

            {isS3Syncing && (
              <button 
                className="s3-cancel-btn"
                onClick={cancelS3Sync}
              >
                ❌ Cancel Sync
              </button>
            )}
          </div>

          {/* Sync Stats */}
          {(isS3Syncing || lastSyncTime) && (
            <div className="s3-sync-stats">
              <div className="stat-item">
                <span className="stat-label">Total:</span>
                <span className="stat-value">{syncStats.total}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Downloaded:</span>
                <span className="stat-value success">{syncStats.downloaded}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Errors:</span>
                <span className="stat-value error">{syncStats.errors}</span>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {isS3Syncing && syncStats.total > 0 && (
            <div className="s3-progress-bar">
              <div 
                className="s3-progress-fill" 
                style={{ width: `${(syncStats.downloaded / syncStats.total) * 100}%` }}
              />
            </div>
          )}

          {/* Last Sync Time */}
          {lastSyncTime && !isS3Syncing && (
            <div className="s3-last-sync">
              Last sync: {formatTimeAgo(lastSyncTime)}
            </div>
          )}

          {/* Empty Bucket Info */}
          {emptyBucketSync && !isS3Syncing && (
            <div className="s3-empty-bucket-info">
              ℹ️ S3 bucket is empty - no files to download
            </div>
          )}

          {/* Console Logs */}
          <div className="s3-console">
            <div className="console-header">
              📟 S3 SYNC LOGS
            </div>
            <div ref={consoleRef} className="console-output">
              {consoleLogs.length === 0 ? (
                <div className="console-empty">No sync activity</div>
              ) : (
                consoleLogs.map((log, i) => (
                  <div key={i} className={`console-line ${log.level}`}>
                    <span className="time">[{log.timestamp.split(' ')[1]}]</span>
                    <span className="msg">{log.message}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Proceed To Printing Button - Only show when S3 sync completed successfully */}
          {hasCompletedS3Sync && !isS3Syncing && (
            <div className="proceed-to-printing-container">
              <button 
                className="proceed-to-printing-btn"
                onClick={proceedToPrinting}
              >
                ➡️ Proceed To Printing
              </button>
            </div>
          )}
        </div>
            </div>
          )}
        </div>

        {/* Print Cards Accordion */}
        <div className="accordion-section">
          <div 
            className="accordion-header" 
            onClick={() => {
              setIsPrintExpanded(!isPrintExpanded);
              if (!isPrintExpanded) {
                setIsScreenshotExpanded(false);
                setIsS3Expanded(false);
              }
            }}
          >
            <span className="accordion-title">🖨️ Print Cards</span>
            <span className="accordion-icon">{isPrintExpanded ? '▼' : '▶'}</span>
          </div>
          
          {isPrintExpanded && (
            <div className="accordion-content">
              {/* Print Cards Section */}
              <div className="print-section">
                {/* Print Controls */}
                <div className="print-controls">
                  <button 
                    className="print-btn"
                    onClick={startPrint}
                    disabled={isPrinting || !selectedLocation || !hasCompletedS3Sync}
                    title={!hasCompletedS3Sync ? 'Complete S3 Sync first' : ''}
                  >
                    {isPrinting ? '⏳ Printing...' : '🖨️ Start Print Workflow'}
                  </button>
                  
                  {!hasCompletedS3Sync && (
                    <div className="workflow-hint">
                      ℹ️ Please complete S3 Sync before starting print workflow
                    </div>
                  )}
                </div>

                {/* Print Status */}
                {printStatus && (
                  <div className={`print-status ${printStatus.success === true ? 'success' : printStatus.success === false ? 'error' : ''}`}>
                    <div className="status-phase">Phase: {printStatus.phase}</div>
                    <div className="status-message">{printStatus.message}</div>
                  </div>
                )}

                {/* Last Print Time */}
                {lastPrintTime && !isPrinting && (
                  <div className="print-last-run">
                    Last print: {formatTimeAgo(lastPrintTime)}
                    {printStatus?.success === true && <span className="success-badge"> ✓ Success</span>}
                    {printStatus?.success === false && <span className="error-badge"> ✗ Failed</span>}
                  </div>
                )}

                {/* Console Logs */}
                <div className="print-console">
                  <div className="console-header">
                    📟 PRINT WORKFLOW LOGS
                  </div>
                  <div ref={printConsoleRef} className="console-output">
                    {printLogs.length === 0 ? (
                      <div className="console-empty">No print activity</div>
                    ) : (
                      printLogs.map((log, i) => (
                        <div key={i} className={`console-line ${log.level}`}>
                          <span className="time">[{log.timestamp.split(' ')[1]}]</span>
                          {' '}
                          <span className="msg">{log.message}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                
                {/* Transfer to Photos Button - Show after workflow completes */}
                {printWorkflowComplete && (
                  <div style={{ 
                    marginTop: '20px', 
                    padding: '20px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px',
                    border: '2px solid #9B59B6'
                  }}>
                    <p style={{ 
                      marginBottom: '15px', 
                      fontWeight: 'bold',
                      color: '#2c3e50',
                      fontSize: '15px'
                    }}>
                      ✅ Print workflow complete! Cards should be printing now.
                    </p>
                    
                    <button
                      onClick={transferImagesToPhotos}
                      disabled={isTransferring}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#9B59B6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: isTransferring ? 'not-allowed' : 'pointer',
                        opacity: isTransferring ? 0.6 : 1,
                        width: '100%',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        if (!isTransferring) {
                          e.currentTarget.style.backgroundColor = '#8e44ad';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#9B59B6';
                      }}
                    >
                      {isTransferring ? '⏳ Transferring Images...' : '📸 All Cards Printed - Transfer to Photos'}
                    </button>
                    
                    <p style={{ 
                      marginTop: '10px', 
                      fontSize: '13px',
                      color: '#dc3545',
                      fontWeight: 'bold'
                    }}>
                      ⚠️ Click this button only after all cards have been printed
                    </p>
                    
                    {/* Transfer Console - Shows logs during/after transfer */}
                    {transferLogs.length > 0 && (
                      <div style={{ marginTop: '15px' }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#2c3e50',
                          marginBottom: '5px',
                          padding: '8px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '4px'
                        }}>
                          📟 TRANSFER LOGS
                        </div>
                        <div 
                          ref={transferConsoleRef}
                          style={{
                            backgroundColor: '#1e1e1e',
                            color: '#d4d4d4',
                            fontFamily: 'Consolas, monospace',
                            fontSize: '12px',
                            padding: '10px',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            border: '1px solid #333',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {transferLogs.map((log, i) => (
                            <div key={i} style={{ marginBottom: '2px' }}>
                              {log}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Fullscreen Modal */}
      {isFullscreen && screenshot && (
        <div className="cp-fullscreen-modal" onClick={closeFullscreen}>
          <div className="cp-fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <button className="cp-fullscreen-close" onClick={closeFullscreen}>
              ✕ Close
            </button>
            <img 
              src={screenshot} 
              alt="Windows Desktop Screenshot - Fullscreen" 
              className="cp-fullscreen-image"
            />
            <div className="cp-fullscreen-info">
              <span>{selectedLocation}</span>
              <span>•</span>
              <span>Captured: {lastCaptured && formatTimeAgo(lastCaptured)}</span>
            </div>
          </div>
        </div>
      )}

      {/* NOTE: Print Confirmation Modal removed - Phase 7 of PrintStudioWorkflow.ahk
          now automatically sends Left Ctrl+P at the end, no manual confirmation needed */}
    </>
  );
};
